<template>
  <main class="app-content">
    <div class="app-title">
      <h1><font-awesome-icon icon="comments"/> AI Agent - Tổng quan</h1>
    </div>

    <div class="row mb-3">
      <div class="col-md-4">
        <b-form-group label="Từ ngày">
          <b-form-input type="date" v-model="filterFrom"/>
        </b-form-group>
      </div>
      <div class="col-md-4">
        <b-form-group label="Đến ngày">
          <b-form-input type="date" v-model="filterTo"/>
        </b-form-group>
      </div>
      <div class="col-md-4 d-flex align-items-end">
        <b-button variant="primary" @click="load" class="mb-3">
          <font-awesome-icon icon="search"/> Xem
        </b-button>
      </div>
    </div>

    <!-- Alert banner -->
    <b-alert v-if="alert && alert.isAlert" variant="danger" show dismissible class="mb-4">
      <h5 class="mb-1"><font-awesome-icon icon="exclamation-triangle"/> Cảnh báo: Tỉ lệ lỗi cao!</h5>
      <p class="mb-1">Trong 1 giờ qua: <strong>{{ alert.error }}/{{ alert.total }}</strong> tin nhắn bị lỗi ({{ alert.errorRate }}%)</p>
      <div v-if="alert.recentErrors && alert.recentErrors.length" class="mt-2">
        <small><strong>Lỗi gần đây:</strong></small>
        <ul class="mb-1 pl-3">
          <li v-for="(err, i) in alert.recentErrors.slice(0, 5)" :key="i">
            <small>
              {{ err.createTime | moment('HH:mm') }} -
              <span v-if="err.client">{{ err.client.name }}</span>
              <span v-if="err.bot"> / {{ err.bot.name }}</span>
              <span v-if="err.customer"> — KH: {{ err.customer.name }}</span>
              <span class="text-danger" v-if="err.errorMsg"> — {{ err.errorMsg.substring(0, 80) }}</span>
            </small>
          </li>
        </ul>
      </div>
    </b-alert>

    <!-- Summary cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Tổng tin nhắn</h5>
          <h2 class="mb-0">{{ stats.total || 0 }}</h2>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-success mb-1">Đã gửi</h5>
          <h2 class="mb-0 text-success">{{ stats.sent || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.sent / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-danger mb-1">Lỗi</h5>
          <h2 class="mb-0 text-danger">{{ stats.error || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.error / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-warning mb-1">Tool lỗi</h5>
          <h2 class="mb-0 text-warning">{{ stats.toolErrors || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.toolErrors / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Thời gian TB</h5>
          <h2 class="mb-0">{{ stats.avgDuration ? (stats.avgDuration / 1000).toFixed(1) + 's' : '—' }}</h2>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Tổng token</h5>
          <h2 class="mb-0">{{ (stats.totalTokens || 0).toLocaleString() }}</h2>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Chi phí ước tính</h5>
          <h2 class="mb-0">${{ estimatedCost }}</h2>
        </div>
      </div>
    </div>

    <!-- Charts row -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="card p-3">
          <h5>Xu hướng sử dụng</h5>
          <apexchart v-if="trendOptions.xaxis.categories.length" type="bar" height="300" :options="trendOptions" :series="trendSeries"/>
          <p v-else class="text-muted text-center py-5">Chưa có dữ liệu</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3">
          <h5>Nền tảng</h5>
          <div v-if="stats.platforms && stats.platforms.length">
            <div v-for="p in stats.platforms" :key="p._id" class="d-flex justify-content-between py-1 border-bottom">
              <span>{{ platformLabels[p._id] || p._id }}</span>
              <b-badge variant="primary">{{ p.count }}</b-badge>
            </div>
          </div>
          <p v-else class="text-muted text-center py-5">Chưa có dữ liệu</p>
        </div>
      </div>
    </div>

    <!-- Top actions -->
    <div class="row mb-4" v-if="stats.topActions && stats.topActions.length">
      <div class="col-md-12">
        <div class="card p-3">
          <h5>Top Tools sử dụng</h5>
          <div class="d-flex flex-wrap">
            <div v-for="a in stats.topActions" :key="a._id" class="mr-3 mb-2">
              <b-badge variant="info" class="mr-1">{{ a._id }}</b-badge>
              <small class="text-muted">{{ a.count }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Top bots -->
      <div class="col-md-6">
        <div class="card p-3">
          <h5>Top Bot</h5>
          <b-table v-if="stats.topBots && stats.topBots.length" :items="stats.topBots" :fields="botFields" small striped>
            <template #cell(name)="data">
              {{ data.item.name || '—' }}
              <b-badge v-if="!data.item.active" variant="secondary" class="ml-1">Tắt</b-badge>
            </template>
            <template #cell(errors)="data">
              <span :class="data.value > 0 ? 'text-danger' : ''">{{ data.value }}</span>
            </template>
          </b-table>
          <p v-else class="text-muted text-center py-3">Chưa có dữ liệu</p>
        </div>
      </div>
      <!-- Top clients -->
      <div class="col-md-6">
        <div class="card p-3">
          <h5>Top khách hàng</h5>
          <b-table v-if="stats.topClients && stats.topClients.length" :items="stats.topClients" :fields="clientFields" small striped>
            <template #cell(tokens)="data">
              {{ (data.value || 0).toLocaleString() }}
            </template>
          </b-table>
          <p v-else class="text-muted text-center py-3">Chưa có dữ liệu</p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  data() {
    var now = new Date();
    var thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      filterFrom: thirtyDaysAgo.toISOString().split('T')[0],
      filterTo: now.toISOString().split('T')[0],
      stats: {},
      alert: null,
      botFields: [
        { key: 'name', label: 'Bot' },
        { key: 'count', label: 'Tin nhắn', class: 'text-right' },
        { key: 'errors', label: 'Lỗi', class: 'text-right' },
      ],
      clientFields: [
        { key: 'name', label: 'Khách hàng' },
        { key: 'domain', label: 'Domain' },
        { key: 'count', label: 'Tin nhắn', class: 'text-right' },
        { key: 'tokens', label: 'Tokens', class: 'text-right' },
      ],
      platformLabels: {
        facebook: 'Facebook',
        instagram: 'Instagram',
        zalo: 'Zalo OA',
        telegram: 'Telegram',
        whatsapp: 'WhatsApp',
        livechat: 'Live Chat',
      },
    };
  },
  computed: {
    estimatedCost() {
      var prompt = (this.stats.totalPromptTokens || 0) / 1000000 * 2.5;
      var completion = (this.stats.totalCompletionTokens || 0) / 1000000 * 10;
      return (prompt + completion).toFixed(2);
    },
    trendOptions() {
      var trend = (this.stats.trend || []);
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        xaxis: { categories: trend.map(function (t) { return t._id; }) },
        colors: ['#28a745', '#dc3545'],
        plotOptions: { bar: { columnWidth: '60%' } },
      };
    },
    trendSeries() {
      var trend = (this.stats.trend || []);
      return [
        { name: 'Đã gửi', data: trend.map(function (t) { return t.sent; }) },
        { name: 'Lỗi', data: trend.map(function (t) { return t.error; }) },
      ];
    },
  },
  methods: {
    load() {
      var from = new Date(this.filterFrom).getTime();
      var to = new Date(this.filterTo).getTime() + 24 * 60 * 60 * 1000 - 1;
      this.$service.get('/ai-agent/stats', { params: { from: from, to: to } })
        .then(res => {
          this.stats = res.data;
        });
      this.$service.get('/ai-agent/alert')
        .then(res => {
          this.alert = res.data;
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>
